import { useEffect, useMemo, useReducer, useState, type CSSProperties, type FormEvent } from "react"
import {
  useTenant,
  useUpdateTenantProfile,
  useQuietHours,
  useSetQuietHours,
  useDisconnectStripe,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useStatusHealth,
  useBillingPlans,
  useBillingUsage,
  useChangeBillingPlan,
  useUpdateBillingAddons,
  useCreateBillingPortalSessionMutation,
  useCreateBillingCheckoutMutation,
  useTeamMembers,
  useAddTeamMember,
  useRemoveTeamMember,
} from "../../hooks/useApi"
import { useTheme } from "../../theme/ThemeProvider"
import {
  DEFAULT_NOTIFICATION_PREFERENCES_STATE,
  notificationPreferencesReducer,
} from "../../state/notificationPreferences"
import { buildQuietHoursPayload } from "../../utils/notifications"
import { DegradedNotice } from "../../components/DegradedNotice"
import { useConnectStripe } from "../../hooks/useConnectStripe"
import { trackDegradedRails, trackDigestToggle } from "../../utils/telemetry"
import DrawerPanel from "../../components/Drawer"
import { PRICING_PLANS } from "../../data/pricing"
import { ApiError } from "../../api/http"

export default function SettingsPage() {
  const teamQuery = useTeamMembers()
  const addTeamMember = useAddTeamMember()
  const removeTeamMember = useRemoveTeamMember()
  const [teamName, setTeamName] = useState("")
  const [teamPhone, setTeamPhone] = useState("")
  const [teamError, setTeamError] = useState<string | null>(null)
  const { theme } = useTheme()
  const isFairy = theme === "fairy"
  const { data: tenant, isLoading: tenantLoading } = useTenant()
  const { data: quietHours, isLoading: quietLoading } = useQuietHours()

  const updateProfile = useUpdateTenantProfile()
  const setQuietHours = useSetQuietHours()
  const disconnectStripe = useDisconnectStripe()

  const {
    connect: handleConnectStripe,
    connecting: stripeConnecting,
    error: stripeError,
  } = useConnectStripe()
  const notificationPreferences = useNotificationPreferences()
  const updateNotificationPreferences = useUpdateNotificationPreferences()
  const statusHealth = useStatusHealth()
  const billingPlans = useBillingPlans()
  const billingUsage = useBillingUsage()
  const changePlanMutation = useChangeBillingPlan()
  const updateAddonsMutation = useUpdateBillingAddons()
  const portalSessionMutation = useCreateBillingPortalSessionMutation()
  const checkoutMutation = useCreateBillingCheckoutMutation()

  const planSlug = useMemo(() => {
    const slug = billingUsage.data?.plan?.slug ?? tenant?.stripe_plan_id ?? ""
    return typeof slug === "string" ? slug.toLowerCase() : ""
  }, [billingUsage.data?.plan?.slug, tenant?.stripe_plan_id])

  const currentPlan = useMemo(() => {
    if (billingUsage.data?.plan) {
      return billingUsage.data.plan
    }
    if (!planSlug) return undefined
    const catalog = billingPlans.data?.plans ?? []
    return catalog.find((plan) => plan.slug === planSlug)
  }, [billingPlans.data, billingUsage.data, planSlug])

  const activeAddons = useMemo(() => billingUsage.data?.active_addons ?? [], [billingUsage.data?.active_addons])
  const addonLookup = useMemo(() => {
    const catalogAddons = billingPlans.data?.addons ?? []
    return catalogAddons.reduce<Record<string, (typeof catalogAddons)[number]>>((map, addon) => {
      map[addon.slug] = addon
      return map
    }, {})
  }, [billingPlans.data?.addons])

  const planCatalog = billingPlans.data?.plans ?? []
  const addonCatalog = billingPlans.data?.addons ?? []
  const hasBillingAccount = Boolean(tenant?.demo) || Boolean(tenant?.stripe_subscription_id)
  const pricingContentBySlug = useMemo(() => {
    return PRICING_PLANS.reduce<Record<string, (typeof PRICING_PLANS)[number]>>((map, plan) => {
      map[plan.slug] = plan
      return map
    }, {})
  }, [])

  const handleOpenPlanModal = () => {
    const next = planSlug || planCatalog[0]?.slug || null
    setSelectedPlanSlug(next)
    setPlanModalOpen(true)
    setBillingActionError(null)
  }

  const handleOpenAddonsModal = () => {
    setSelectedAddonSlugs(activeAddons)
    setAddonsModalOpen(true)
    setBillingActionError(null)
  }

  const handleConfirmPlanChange = async () => {
    if (!selectedPlanSlug) return
    setBillingActionError(null)
    try {
      if (!hasBillingAccount) {
        const url = await checkoutMutation.mutateAsync(selectedPlanSlug)
        window.location.assign(url)
        return
      }
      await changePlanMutation.mutateAsync(selectedPlanSlug)
      setPlanModalOpen(false)
    } catch (error) {
      console.error("Failed to update plan", error)
      if (error instanceof ApiError && error.message.includes("plan_not_synced")) {
        setBillingActionError("This plan isn't connected to Stripe yet. Run the catalog sync, then retry.")
      } else if (error instanceof ApiError && error.message.includes("no_billing_account")) {
        setBillingActionError("No billing account yet \u2014 start with checkout below.")
      } else {
        setBillingActionError(hasBillingAccount ? "Failed to update plan. Try again in a moment." : "Checkout could not start. Try again in a moment.")
      }
    }
  }

  const handleToggleAddon = (slug: string) => {
    setSelectedAddonSlugs((prev) => {
      if (prev.includes(slug)) {
        return prev.filter((entry) => entry !== slug)
      }
      return [...prev, slug]
    })
  }

  const handleConfirmAddonUpdate = async () => {
    setBillingActionError(null)
    try {
      await updateAddonsMutation.mutateAsync(selectedAddonSlugs)
      setAddonsModalOpen(false)
    } catch (error) {
      console.error("Failed to update add-ons", error)
      setBillingActionError("Failed to update add-ons. Retry shortly.")
    }
  }

  const handlePortalLaunch = async () => {
    setBillingActionError(null)
    try {
      const url = await portalSessionMutation.mutateAsync()
      if (url) {
        window.open(url, "_blank", "noopener")
      }
    } catch (error) {
      console.error("Failed to open billing portal", error)
      if (error instanceof ApiError && error.status === 409) {
        setBillingActionError("No billing account yet \u2014 the portal unlocks after your first subscription.")
      } else {
        setBillingActionError("Unable to reach the billing portal. Try again soon.")
      }
    }
  }

  const billingBusy = billingPlans.isLoading || billingUsage.isLoading
  const billingMutating = changePlanMutation.isPending || updateAddonsMutation.isPending
  const planButtonDisabled = billingBusy || planCatalog.length === 0
  const addonsButtonDisabled = billingBusy || addonCatalog.length === 0

  const panelStyles = useMemo(() => buildPanelStyles(isFairy), [isFairy])

  const usagePanelStyle: CSSProperties = {
    ...panelStyles.container,
    gap: "18px",
  }
  const twoColumnStyle: CSSProperties = {
    display: "grid",
    gap: "clamp(16px, 3vw, 20px)",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  }
  const addonChipStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "999px",
    border: isFairy ? "1px solid rgba(46,214,181,0.35)" : "1px solid rgba(255,43,79,0.45)",
    background: "transparent",
    fontSize: "12px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  }
  const billingErrorStyle: CSSProperties = {
    border: "1px solid rgba(255,43,79,0.45)",
    background: "rgba(255,43,79,0.12)",
    color: "var(--error-text)",
    padding: "10px 12px",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    fontSize: "12px",
  }

  const usageSummary = billingUsage.data?.usage
  const includedMessages = usageSummary?.included_messages ?? currentPlan?.included_messages ?? 0
  const usageRatio = useMemo(() => {
    if (!usageSummary) return null
    if (typeof usageSummary.percent_of_quota === "number") {
      return usageSummary.percent_of_quota
    }
    if (currentPlan?.included_messages) {
      return usageSummary.month_to_date / currentPlan.included_messages
    }
    return null
  }, [usageSummary, currentPlan])
  const cappedRatio = usageRatio != null ? Math.min(usageRatio, 1) : 0
  const usageLoading = billingUsage.isLoading || billingPlans.isLoading
  const usageError = billingUsage.isError
  const usageEmpty = !usageSummary && !currentPlan
  const last24h = usageSummary?.last_24h ?? 0
  const monthToDate = usageSummary?.month_to_date ?? 0
  const overageRate = usageSummary?.overage_rate ?? currentPlan?.overage_rate ?? 0
  const overageLabel = overageRate > 0 ? `$${overageRate.toFixed(3)} per SMS` : "Included in plan"
  const overAllocation = usageRatio != null && usageRatio > 1
  const overBy = overAllocation && includedMessages ? monthToDate - includedMessages : null

  const [name, setName] = useState("")
  const [supportNumber, setSupportNumber] = useState("")
  const [brandLogo, setBrandLogo] = useState("")
  const [allowWebSend, setAllowWebSend] = useState(true)
  const [planModalOpen, setPlanModalOpen] = useState(false)
  const [addonsModalOpen, setAddonsModalOpen] = useState(false)
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null)
  const [selectedAddonSlugs, setSelectedAddonSlugs] = useState<string[]>([])
  const [billingActionError, setBillingActionError] = useState<string | null>(null)

  const [quietEnabled, setQuietEnabled] = useState(false)
  const [quietStart, setQuietStart] = useState("20:00")
  const [quietEnd, setQuietEnd] = useState("07:00")
  const [quietTimezone, setQuietTimezone] = useState("UTC")
  const [notificationState, dispatchNotifications] = useReducer(
    notificationPreferencesReducer,
    DEFAULT_NOTIFICATION_PREFERENCES_STATE,
  )

  useEffect(() => {
    if (!tenant) return
    setName(tenant.name)
    setSupportNumber(tenant.support_number ?? "")
    setBrandLogo(tenant.brand_logo_url ?? "")
    setAllowWebSend(tenant.allow_web_send !== false)
  }, [tenant])

  useEffect(() => {
    if (!quietHours) return
    setQuietEnabled(quietHours.enabled)
    setQuietStart(quietHours.start_time ?? "20:00")
    setQuietEnd(quietHours.end_time ?? "07:00")
    setQuietTimezone(quietHours.timezone ?? "UTC")
  }, [quietHours])

  useEffect(() => {
    if (!notificationPreferences.data) return
    dispatchNotifications({ type: "hydrate", payload: notificationPreferences.data })
  }, [notificationPreferences.data])

  const handleProfileSubmit = (event: FormEvent) => {
    event.preventDefault()
    updateProfile.mutate({
      name,
      support_number: supportNumber || null,
      brand_logo_url: brandLogo || null,
      allow_web_send: allowWebSend,
    })
  }

  const handleQuietSubmit = (event: FormEvent) => {
    event.preventDefault()
    setQuietHours.mutate({
      enabled: quietEnabled,
      start_time: quietEnabled ? quietStart : null,
      end_time: quietEnabled ? quietEnd : null,
      timezone: quietTimezone,
    })
  }

  const handleNotificationSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)
    const weeklyDigestSelected = formData.get("weekly_digest") === "on"
    const smsEnabled = formData.get("channel_sms") === "on"
    const emailEnabled = formData.get("channel_email") === "on"
    const webEnabled = formData.get("channel_web") === "on"
    const quietEnabledNext = formData.get("quiet_enabled") === "on"
    const quietStartValue = (formData.get("quiet_start") as string | null) ?? notificationState.quietHoursStart
    const quietEndValue = (formData.get("quiet_end") as string | null) ?? notificationState.quietHoursEnd

    const payload = {
      weekly_digest: weeklyDigestSelected,
      channels: {
        sms: {
          digest_enabled: smsEnabled,
          quiet_hours: buildQuietHoursPayload(quietEnabledNext, quietStartValue, quietEndValue),
        },
        email: {
          digest_enabled: emailEnabled,
        },
        web: {
          digest_enabled: webEnabled,
        },
      },
    }
    if (notificationState.weeklyDigest !== weeklyDigestSelected) {
      trackDigestToggle("weekly", weeklyDigestSelected)
    }
    if (notificationState.channels.sms !== smsEnabled) {
      trackDigestToggle("sms", smsEnabled)
    }
    if (notificationState.channels.email !== emailEnabled) {
      trackDigestToggle("email", emailEnabled)
    }
    if (notificationState.channels.web !== webEnabled) {
      trackDigestToggle("web", webEnabled)
    }
    updateNotificationPreferences.mutate(payload)
  }

  const notificationLastUpdated = notificationPreferences.data?.last_updated_at
    ?? notificationPreferences.data?.weekly_digest_updated_at
    ?? null
  const digestDisabled = !notificationState.weeklyDigest
  const degradedBanner = statusHealth.data?.degraded_banner
  const degradedRails = useMemo(() => degradedBanner?.rails ?? [], [degradedBanner?.rails])

  useEffect(() => {
    if (degradedBanner?.state === "degraded" && degradedRails.length) {
      trackDegradedRails(degradedRails, "settings.notifications")
    }
  }, [degradedBanner?.state, degradedRails])

  return (
    <>
      <DrawerPanel
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        side="right"
        title="Change plan"
      >
        <div style={{ display: "grid", gap: "16px" }}>
          {billingPlans.isLoading ? (
            <div className="small" style={panelStyles.muted}>Loading available plans…</div>
          ) : planCatalog.length === 0 ? (
            <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
              No plans available. Configure the catalog first.
            </div>
          ) : (
            planCatalog.map((plan) => {
              const selected = selectedPlanSlug === plan.slug
              const isCurrent = planSlug === plan.slug
              const content = pricingContentBySlug[plan.slug]
              const monthly = plan.base_price_cents ? Math.round(plan.base_price_cents / 100) : null
              return (
                <label
                  key={plan.slug}
                  style={{
                    border: selected ? "2px solid var(--accent)" : "1px solid var(--rail)",
                    padding: selected ? "15px" : "16px",
                    display: "grid",
                    gap: "8px",
                    cursor: "pointer",
                    background: selected ? "var(--panel)" : "transparent",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "baseline", gap: "12px", justifyContent: "space-between" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input
                        type="radio"
                        name="billing_plan"
                        value={plan.slug}
                        checked={selected}
                        onChange={() => setSelectedPlanSlug(plan.slug)}
                      />
                      <span className="mono" style={{ fontSize: "15px", letterSpacing: "0.16em", textTransform: "uppercase" }}>
                        {plan.name}
                      </span>
                      {isCurrent ? (
                        <span
                          className="mono"
                          style={{
                            fontSize: "10px",
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            background: "var(--accent)",
                            color: "var(--text-on-accent)",
                            padding: "2px 8px",
                          }}
                        >
                          Current
                        </span>
                      ) : null}
                    </span>
                    {monthly !== null ? (
                      <span className="mono" style={{ fontSize: "20px", fontWeight: 700 }}>
                        ${monthly}
                        <span style={{ fontSize: "11px", fontWeight: 400, color: "var(--muted)" }}>/mo</span>
                      </span>
                    ) : null}
                  </span>
                  {content ? (
                    <span className="small" style={{ textTransform: "none" as const, color: "var(--muted)" }}>
                      {content.subtitle}
                    </span>
                  ) : null}
                  <span className="small" style={{ textTransform: "none" as const, color: "var(--muted)" }}>
                    {plan.included_messages ? `${plan.included_messages.toLocaleString()} messages included` : "Metered plan"}
                    {typeof plan.overage_rate === "number" && plan.overage_rate > 0
                      ? ` · then $${plan.overage_rate.toFixed(3)}/SMS carrier passthrough`
                      : ""}
                  </span>
                </label>
              )
            })
          )}
          {billingActionError ? (
            <div className="small" role="alert" aria-live="polite" style={billingErrorStyle}>
              {billingActionError}
            </div>
          ) : null}
          <div className="small" style={{ textTransform: "none" as const, color: "var(--muted)" }}>
            {hasBillingAccount
              ? "Plan changes are prorated by Stripe on your next invoice."
              : "You’ll confirm payment on a secure Stripe checkout page."}
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              type="button"
              className="mono"
              onClick={handleConfirmPlanChange}
              disabled={
                changePlanMutation.isPending
                || checkoutMutation.isPending
                || !selectedPlanSlug
                || (hasBillingAccount && selectedPlanSlug === planSlug)
              }
              style={primaryButton(isFairy)}
            >
              {checkoutMutation.isPending || changePlanMutation.isPending
                ? "Working…"
                : !hasBillingAccount
                  ? "Continue to checkout"
                  : selectedPlanSlug === planSlug
                    ? "Current plan"
                    : "Switch plan"}
            </button>
            <button
              type="button"
              className="mono"
              onClick={() => setPlanModalOpen(false)}
              style={secondaryButton(isFairy)}
            >
              Cancel
            </button>
          </div>
        </div>
      </DrawerPanel>

      <DrawerPanel
        open={addonsModalOpen}
        onClose={() => setAddonsModalOpen(false)}
        side="right"
        title="Manage add-ons"
      >
        <div style={{ display: "grid", gap: "16px" }}>
          {billingPlans.isLoading ? (
            <div className="small" style={panelStyles.muted}>Loading add-ons…</div>
          ) : addonCatalog.length === 0 ? (
            <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
              No add-ons configured yet.
            </div>
          ) : (
            addonCatalog.map((addon) => {
              const selected = selectedAddonSlugs.includes(addon.slug)
              const monthly = ((addon.unit_amount_cents ?? 0) / 100).toFixed(2)
              return (
                <label
                  key={addon.slug}
                  style={{
                    border: selected ? "2px solid var(--accent)" : "1px solid var(--rail)",
                    padding: selected ? "15px" : "16px",
                    display: "grid",
                    gap: "8px",
                    cursor: hasBillingAccount ? "pointer" : "not-allowed",
                    opacity: hasBillingAccount ? 1 : 0.55,
                    background: selected ? "var(--panel)" : "transparent",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "baseline", gap: "12px", justifyContent: "space-between" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input
                        type="checkbox"
                        name="billing_addon"
                        value={addon.slug}
                        checked={selected}
                        disabled={!hasBillingAccount}
                        onChange={() => handleToggleAddon(addon.slug)}
                      />
                      <span className="mono" style={{ fontSize: "14px", letterSpacing: "0.14em", textTransform: "uppercase" }}>
                        {addon.name}
                      </span>
                    </span>
                    <span className="mono" style={{ fontSize: "16px", fontWeight: 700 }}>
                      ${monthly}
                      <span style={{ fontSize: "11px", fontWeight: 400, color: "var(--muted)" }}>/{addon.interval}</span>
                    </span>
                  </span>
                  {addon.slug === "extra-seat" ? (
                    <span className="small" style={{ textTransform: "none" as const, color: "var(--muted)" }}>
                      Adds one teammate line beyond your plan’s included seats.
                    </span>
                  ) : null}
                </label>
              )
            })
          )}
          {billingActionError ? (
            <div className="small" role="alert" aria-live="polite" style={billingErrorStyle}>
              {billingActionError}
            </div>
          ) : null}
          <div className="small" style={{ textTransform: "none" as const, color: "var(--muted)" }}>
            {hasBillingAccount
              ? `Add-ons total: $${(selectedAddonSlugs.reduce((sum, slug) => sum + ((addonLookup[slug]?.unit_amount_cents ?? 0) / 100), 0)).toFixed(2)}/month, prorated by Stripe.`
              : "Add-ons unlock after your first subscription."}
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              type="button"
              className="mono"
              onClick={handleConfirmAddonUpdate}
              disabled={updateAddonsMutation.isPending || !hasBillingAccount}
              style={primaryButton(isFairy)}
            >
              {updateAddonsMutation.isPending ? "Updating…" : "Save add-ons"}
            </button>
            <button
              type="button"
              className="mono"
              onClick={() => setAddonsModalOpen(false)}
              style={secondaryButton(isFairy)}
            >
              Cancel
            </button>
          </div>
        </div>
      </DrawerPanel>

      <div style={{ display: "grid", gap: "clamp(20px, 4vw, 28px)" }}>
        <section id="usage" style={usagePanelStyle}>
          <header className="mono" style={panelStyles.header}>Usage</header>
          {usageLoading ? (
            <div className="small" style={panelStyles.muted}>Calculating message usage…</div>
          ) : usageError ? (
            <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
              Unable to load usage. Retry shortly.
            </div>
          ) : usageEmpty ? (
            <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
              Billing plan not configured. Connect Stripe to enable usage tracking.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: "16px",
                }}
              >
                <div>
                  <span className="mono small" style={{ letterSpacing: "0.12em", color: panelStyles.muted.color }}>
                    Current plan
                  </span>
                  <div style={{ fontSize: "22px", letterSpacing: "0.08em" }}>{currentPlan?.name ?? "Unassigned"}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span className="mono small" style={{ letterSpacing: "0.08em", color: panelStyles.muted.color }}>
                    Current period
                  </span>
                  <div style={{ fontSize: "22px", letterSpacing: "0.08em" }}>{monthToDate.toLocaleString()}</div>
                </div>
              </div>
              <div
                style={{
                  height: "14px",
                  borderRadius: "999px",
                  border: isFairy
                    ? "1px solid rgba(46,214,181,0.35)"
                    : "1px solid rgba(255,43,79,0.35)",
                  overflow: "hidden",
                  position: "relative",
                  background: "transparent",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(cappedRatio, 1) * 100}%`,
                    background: overAllocation ? "rgba(255,43,79,0.85)" : "var(--rail)",
                    transition: "width 180ms ease-out",
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  justifyContent: "space-between",
                }}
              >
                <span className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                  Included {includedMessages ? `${includedMessages.toLocaleString()} SMS` : "metered"}
                </span>
                <span className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                  Last 24h: {last24h.toLocaleString()} SMS
                </span>
                <span className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                  Overage: {overageLabel}
                </span>
              </div>
              {overAllocation && overBy !== null ? (
                <div className="mono small" style={billingErrorStyle}>
                  Over quota by {overBy.toLocaleString()} SMS — consider upgrading.
                </div>
              ) : null}
            </>
          )}
        </section>

        <div style={twoColumnStyle}>
          <section id="profile" style={panelStyles.container}>
            <header className="mono" style={panelStyles.header}>Profile</header>
            {tenantLoading ? (
              <div className="small" style={panelStyles.muted}>Loading persona details…</div>
            ) : (
              <form onSubmit={handleProfileSubmit} style={{ display: "grid", gap: "16px" }}>
                <label style={labelStyle}>
                  <span>Name</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                    style={inputStyle(isFairy)}
                  />
                </label>
                <label style={labelStyle}>
                  <span>Connected number</span>
                  <input
                    type="tel"
                    value={supportNumber}
                    onChange={(event) => setSupportNumber(event.target.value)}
                    placeholder="+15551234567"
                    style={inputStyle(isFairy)}
                  />
                </label>
                <label style={{ ...labelStyle, flexDirection: "row", alignItems: "center", gap: "12px" }}>
                  <input
                    type="checkbox"
                    checked={allowWebSend}
                    onChange={(event) => setAllowWebSend(event.target.checked)}
                  />
                  <span>Allow web composer to send messages</span>
                </label>
                <button type="submit" className="mono" style={primaryButton(isFairy)} disabled={updateProfile.isPending}>
                  Save profile
                </button>
              </form>
            )}
          </section>

          <section id="team" style={panelStyles.container}>
            <header className="mono" style={panelStyles.header}>Team</header>
            {teamQuery.isLoading ? (
              <div className="small" style={panelStyles.muted}>Loading team…</div>
            ) : (
              <div style={{ display: "grid", gap: "16px" }}>
                <div className="small" style={panelStyles.muted}>
                  Teammates text or call your agent number from their own phones.
                  {" "}
                  {teamQuery.data ? `${teamQuery.data.seats.used} of ${teamQuery.data.seats.max} seats used.` : ""}
                </div>
                <div style={{ display: "grid", gap: "8px" }}>
                  {(teamQuery.data?.members ?? []).filter((m) => m.status === "active").map((member) => (
                    <div
                      key={member.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        border: "1px solid var(--rail)",
                        padding: "10px 14px",
                      }}
                    >
                      <div style={{ display: "grid", gap: "2px", minWidth: 0 }}>
                        <span className="mono" style={{ fontSize: "13px" }}>{member.name || "Teammate"}</span>
                        <span className="mono small" style={{ color: "var(--muted)" }}>{member.phone}</span>
                      </div>
                      <button
                        type="button"
                        className="mono small"
                        onClick={() => removeTeamMember.mutate(member.id)}
                        disabled={removeTeamMember.isPending}
                        style={{
                          border: "1px solid var(--rail)",
                          background: "transparent",
                          color: "var(--muted)",
                          padding: "8px 12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          cursor: "pointer",
                          minHeight: "40px",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {(teamQuery.data?.members ?? []).filter((m) => m.status === "active").length === 0 ? (
                    <div className="small" style={panelStyles.muted}>No teammates yet.</div>
                  ) : null}
                </div>
                <form
                  onSubmit={(event) => {
                    event.preventDefault()
                    if (!teamPhone.trim()) return
                    addTeamMember.mutate(
                      { phone: teamPhone.trim(), name: teamName.trim() || null },
                      {
                        onSuccess: () => {
                          setTeamPhone("")
                          setTeamName("")
                          setTeamError(null)
                        },
                        onError: (error) => {
                          const message = error instanceof Error ? error.message : "Could not add teammate"
                          setTeamError(
                            message.includes("seat_limit") ? "All 5 seats are used. Remove someone first." :
                            message.includes("owner_is_already") ? "That's your own number." :
                            message.includes("member_already") ? "That number is already on the team." :
                            "Could not add teammate. Check the number and try again.",
                          )
                        },
                      },
                    )
                  }}
                  style={{ display: "grid", gap: "12px" }}
                >
                  <label style={labelStyle}>
                    <span>Name</span>
                    <input
                      type="text"
                      value={teamName}
                      onChange={(event) => setTeamName(event.target.value)}
                      placeholder="Dave"
                      style={inputStyle(isFairy)}
                    />
                  </label>
                  <label style={labelStyle}>
                    <span>Phone</span>
                    <input
                      type="tel"
                      value={teamPhone}
                      onChange={(event) => setTeamPhone(event.target.value)}
                      placeholder="555 000 1234"
                      required
                      style={inputStyle(isFairy)}
                    />
                  </label>
                  {teamError ? (
                    <div className="small" style={{ color: "var(--error-text)" }}>{teamError}</div>
                  ) : null}
                  <button type="submit" className="mono" style={primaryButton(isFairy)} disabled={addTeamMember.isPending}>
                    {addTeamMember.isPending ? "Adding…" : "Add teammate"}
                  </button>
                </form>
              </div>
            )}
          </section>

          <section id="billing" style={panelStyles.container}>
            <header className="mono" style={panelStyles.header}>Billing</header>
            {billingBusy ? (
              <div className="small" style={panelStyles.muted}>Loading billing summary…</div>
            ) : (
              <div style={{ display: "grid", gap: "16px" }}>
                <div style={{ display: "grid", gap: "8px" }}>
                  <span className="mono" style={{ letterSpacing: "0.12em", textTransform: "uppercase", color: panelStyles.muted.color }}>
                    Current plan
                  </span>
                  {currentPlan ? (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                        <span className="mono" style={{ fontSize: "20px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                          {currentPlan.name}
                        </span>
                        {currentPlan.base_price_cents ? (
                          <span className="mono" style={{ fontSize: "16px", fontWeight: 700 }}>
                            ${Math.round(currentPlan.base_price_cents / 100)}
                            <span style={{ fontSize: "11px", fontWeight: 400, color: "var(--muted)" }}>/mo</span>
                          </span>
                        ) : null}
                      </div>
                      <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                        {includedMessages
                          ? `${includedMessages.toLocaleString()} messages included each month`
                          : "Usage billed as metered add-on"}
                      </div>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="mono"
                      style={secondaryButton(isFairy)}
                      onClick={handleOpenPlanModal}
                      disabled={planButtonDisabled || billingMutating}
                    >
                      Choose plan
                    </button>
                  )}
                </div>
                <div style={{ display: "grid", gap: "10px" }}>
                  <span className="mono small" style={{ letterSpacing: "0.1em", color: panelStyles.muted.color }}>
                    Active add-ons
                  </span>
                  {activeAddons.length ? (
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {activeAddons.map((slug) => {
                        const addon = addonLookup[slug]
                        return (
                          <span key={slug} style={addonChipStyle}>
                            {addon?.name ?? slug}
                            {addon?.unit_amount_cents ? (
                              <span style={{ fontSize: "11px", opacity: 0.7 }}>
                                ${Math.round(addon.unit_amount_cents / 100)}/{addon.interval}
                              </span>
                            ) : null}
                          </span>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                      No add-ons active.
                    </div>
                  )}
                </div>
                {billingActionError ? (
                  <div className="small" style={billingErrorStyle}>{billingActionError}</div>
                ) : null}
                <div style={{ display: "grid", gap: "10px" }}>
                  {currentPlan ? (
                    <button
                      type="button"
                      className="mono"
                      style={secondaryButton(isFairy)}
                      onClick={handleOpenPlanModal}
                      disabled={planButtonDisabled || billingMutating}
                    >
                      Change plan
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="mono"
                    style={secondaryButton(isFairy)}
                    onClick={handleOpenAddonsModal}
                    disabled={addonsButtonDisabled || billingMutating}
                  >
                    Manage add-ons
                  </button>
                  {hasBillingAccount ? (
                    <>
                      <button
                        type="button"
                        className="mono"
                        style={secondaryButton(isFairy)}
                        onClick={handlePortalLaunch}
                        disabled={portalSessionMutation.isPending}
                      >
                        {portalSessionMutation.isPending ? "Opening portal…" : "Update payment method"}
                      </button>
                      <a
                        href="/app/billing"
                        className="mono"
                        style={{ ...secondaryButton(isFairy), textDecoration: "none", textAlign: "center" }}
                      >
                        Manage products
                      </a>
                    </>
                  ) : (
                    <div className="small" style={{ textTransform: "none" as const, color: "var(--muted)" }}>
                      Payment method and invoices are managed in the Stripe portal once your subscription starts.
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        <section style={panelStyles.container}>
        <header className="mono" style={panelStyles.header}>Notifications</header>
        {notificationPreferences.isLoading ? (
          <div className="small" style={panelStyles.muted}>Loading preferences…</div>
        ) : (
          <form onSubmit={handleNotificationSubmit} style={{ display: "grid", gap: "16px" }}>
            {degradedBanner?.state === "degraded" ? (
              <DegradedNotice banner={degradedBanner} persona={isFairy ? "fairy" : "demon"} />
            ) : null}
            {digestDisabled ? (
              <div
                className="mono small"
                role="status"
                style={{
                  border: "1px solid rgba(255,43,79,0.45)",
                  padding: "10px 12px",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  background: "rgba(255,43,79,0.08)",
                  color: "var(--error-text)",
                }}
              >
                Weekly digest disabled — beacon will surface this state.
              </div>
            ) : null}

            <label style={{ ...labelStyle, flexDirection: "row", alignItems: "center", gap: "12px" }}>
              <input
                type="checkbox"
                name="weekly_digest"
                checked={notificationState.weeklyDigest}
                onChange={(event) =>
                  dispatchNotifications({ type: "setWeeklyDigest", value: event.target.checked })
                }
              />
              <span>Weekly digest summary</span>
            </label>

            <div style={{ display: "grid", gap: "12px" }}>
              <span className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                Channel delivery
              </span>
              {(
                [
                  { key: "sms" as const, label: "SMS alerts" },
                  { key: "email" as const, label: "Email updates" },
                  { key: "web" as const, label: "In-app" },
                ]
              ).map((channel) => (
                <label
                  key={channel.key}
                  style={{ ...labelStyle, flexDirection: "row", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="checkbox"
                    name={`channel_${channel.key}`}
                    checked={notificationState.channels[channel.key]}
                    onChange={(event) =>
                      dispatchNotifications({ type: "toggleChannel", channel: channel.key, value: event.target.checked })
                    }
                  />
                  <span>{channel.label}</span>
                </label>
              ))}
            </div>

            <div style={{ display: "grid", gap: "12px" }}>
              <span className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                Quiet hours
              </span>
              <label style={{ ...labelStyle, flexDirection: "row", alignItems: "center", gap: "12px", width: "auto" }}>
                <input
                  type="checkbox"
                  name="quiet_enabled"
                  checked={notificationState.quietHoursEnabled}
                  onChange={(event) =>
                    dispatchNotifications({ type: "setQuietHours", enabled: event.target.checked })
                  }
                />
                <span>Pause notifications overnight</span>
              </label>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <label style={labelStyle}>
                  <span>Start</span>
                  <input
                    type="time"
                    name="quiet_start"
                    value={notificationState.quietHoursStart}
                    onChange={(event) =>
                      dispatchNotifications({
                        type: "setQuietHours",
                        enabled: notificationState.quietHoursEnabled,
                        start: event.target.value,
                      })
                    }
                    disabled={!notificationState.quietHoursEnabled}
                    style={inputStyle(isFairy)}
                  />
                </label>
                <label style={labelStyle}>
                  <span>End</span>
                  <input
                    type="time"
                    name="quiet_end"
                    value={notificationState.quietHoursEnd}
                    onChange={(event) =>
                      dispatchNotifications({
                        type: "setQuietHours",
                        enabled: notificationState.quietHoursEnabled,
                        end: event.target.value,
                      })
                    }
                    disabled={!notificationState.quietHoursEnabled}
                    style={inputStyle(isFairy)}
                  />
                </label>
              </div>
            </div>

            {notificationLastUpdated ? (
              <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                Last updated {new Date(notificationLastUpdated).toLocaleString()}
              </div>
            ) : null}

            <button
              type="submit"
              className="mono"
              style={primaryButton(isFairy)}
              disabled={updateNotificationPreferences.isPending}
            >
              {updateNotificationPreferences.isPending ? "Saving…" : "Save notifications"}
            </button>
          </form>
        )}
      </section>

      <section style={panelStyles.container}>
        <header className="mono" style={panelStyles.header}>Quiet hours</header>
        {quietLoading ? (
          <div className="small" style={panelStyles.muted}>Fetching quiet window…</div>
        ) : (
          <form onSubmit={handleQuietSubmit} style={{ display: "grid", gap: "16px" }}>
            <label style={{ ...labelStyle, flexDirection: "row", alignItems: "center", gap: "12px" }}>
              <input
                type="checkbox"
                checked={quietEnabled}
                onChange={(event) => setQuietEnabled(event.target.checked)}
              />
              <span>Defer outbound messages during quiet hours</span>
            </label>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <label style={labelStyle}>
                <span>Start</span>
                <input
                  type="time"
                  value={quietStart}
                  onChange={(event) => setQuietStart(event.target.value)}
                  disabled={!quietEnabled}
                  style={inputStyle(isFairy)}
                />
              </label>
              <label style={labelStyle}>
                <span>End</span>
                <input
                  type="time"
                  value={quietEnd}
                  onChange={(event) => setQuietEnd(event.target.value)}
                  disabled={!quietEnabled}
                  style={inputStyle(isFairy)}
                />
              </label>
              <label style={labelStyle}>
                <span>Timezone</span>
                <input
                  type="text"
                  value={quietTimezone}
                  onChange={(event) => setQuietTimezone(event.target.value)}
                  disabled={!quietEnabled}
                  placeholder="UTC"
                  style={inputStyle(isFairy)}
                />
              </label>
            </div>
            <button type="submit" className="mono" style={primaryButton(isFairy)} disabled={setQuietHours.isPending}>
              Save quiet hours
            </button>
          </form>
        )}
      </section>

      <section style={panelStyles.container}>
        <header className="mono" style={panelStyles.header}>Integrations</header>
        
        <div style={{ display: "grid", gap: "16px" }}>
          <div style={{ display: "grid", gap: "8px" }}>
            <div className="mono small" style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Stripe
            </div>
            {tenant?.payments_ready ? (
              <>
                <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                  Disconnecting Stripe revokes the {isFairy ? "fairy" : "demon"}&apos;s billing access.
                </div>
                <button
                  type="button"
                  className="mono"
                  onClick={() => disconnectStripe.mutate()}
                  disabled={disconnectStripe.isPending}
                  style={secondaryButton(isFairy)}
                >
                  {disconnectStripe.isPending ? "Disconnecting…" : "Disconnect Stripe"}
                </button>
              </>
            ) : (
              <>
                <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
                  Connect Stripe to enable billing and payment processing for your customers.
                </div>
                {stripeError ? (
                  <div className="mono small" style={{ color: "var(--error-text)", padding: "8px 12px", background: "rgba(255,43,79,0.1)", border: "1px solid rgba(255,43,79,0.3)" }}>
                    {stripeError}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="mono"
                  onClick={handleConnectStripe}
                  disabled={stripeConnecting}
                  style={secondaryButton(isFairy)}
                >
                  {stripeConnecting ? "Connecting…" : "Connect Stripe"}
                </button>
              </>
            )}
          </div>

        </div>
      </section>

      <section style={panelStyles.container}>
        <header className="mono" style={{ ...panelStyles.header, color: "var(--error-text)" }}>Danger zone</header>
        <div className="small" style={{ textTransform: "none" as const, color: panelStyles.muted.color }}>
          To delete your account, contact support.
        </div>
        <button type="button" className="mono" style={dangerButton} disabled>
          Delete account (contact support)
        </button>
      </section>
    </div>
    </>
  )
}

function buildPanelStyles(isFairy: boolean) {
  if (isFairy) {
    return {
      container: {
        background: "rgba(255,255,255,0.94)",
        border: "1px solid rgba(46,214,181,0.35)",
        boxShadow: "0 18px 30px rgba(46,214,181,0.12)",
        padding: "clamp(20px, 4vw, 28px)",
        display: "grid",
        gap: "clamp(12px, 3vw, 18px)",
      },
      header: {
        letterSpacing: "0.18em",
        textTransform: "uppercase" as const,
        color: "#1f2d42",
      },
      muted: {
        color: "#4b5a6d",
      },
    }
  }
  return {
      container: {
        background: "rgba(18,18,20,0.85)",
        border: "1px solid rgba(255,43,79,0.45)",
        boxShadow: "0 18px 30px rgba(255,43,79,0.18)",
        padding: "clamp(20px, 4vw, 28px)",
        display: "grid",
        gap: "clamp(12px, 3vw, 18px)",
      },
    header: {
      letterSpacing: "0.18em",
      textTransform: "uppercase" as const,
      color: "var(--text)",
    },
    muted: {
      color: "var(--muted)",
    },
  }
}

const labelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  fontSize: "13px",
  letterSpacing: "0.04em",
  width: "min(100%, 480px)",
}

function inputStyle(isFairy: boolean): CSSProperties {
  return {
    padding: "12px",
    border: isFairy ? "1px solid rgba(46,214,181,0.45)" : "1px solid rgba(255,43,79,0.45)",
    background: "transparent",
    color: isFairy ? "#1f2d42" : "var(--text)",
    width: "100%",
    minHeight: "44px",
  }
}

function primaryButton(isFairy: boolean): CSSProperties {
  return {
    padding: "12px 18px",
    border: "2px solid var(--rail)",
    background: isFairy ? "rgba(244,255,251,0.95)" : "var(--rail)",
    color: isFairy ? "#1f2d42" : "#0a0a0b",
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    cursor: "pointer",
    minHeight: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }
}

function secondaryButton(isFairy: boolean): CSSProperties {
  return {
    padding: "12px 18px",
    border: isFairy ? "2px solid rgba(46,214,181,0.35)" : "2px solid rgba(255,43,79,0.45)",
    background: "transparent",
    color: isFairy ? "#1f2d42" : "var(--text)",
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    cursor: "pointer",
    minHeight: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  }
}

const dangerButton: CSSProperties = {
  padding: "12px 18px",
  border: "2px solid rgba(255,43,79,0.45)",
  background: "transparent",
  color: "var(--error-text)",
  letterSpacing: "0.16em",
  textTransform: "uppercase" as const,
  cursor: "not-allowed",
  opacity: 0.6,
  minHeight: "44px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
}
